import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { IThemeColors, CSSVariables } from '../models/interfaces';
import {
  ThemeItem,
  ThemeName,
  blueTheme,
  yellowTheme,
  greenTheme,
  redTheme
} from '../models/themes';
import { TranslateService } from '@ngx-translate/core';

import { LocalStorageService } from './local-storage.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ThemeBroadcastService } from './theme-broadcast.service';
import { ConfigKeys } from '../models/configuration';
@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  public themeStorageKey = 'user_theme'; // almacenar el thema en el localStorage
  public themeColorStorageKey = 'user_color_theme'; // almacenar el color del thema en el localStorage

  public currentThemeSubject: BehaviorSubject<ThemeName>;
  public currentTheme$: Observable<ThemeName>;

  private defaultTheme = ThemeName.Blue;
  public colorSaved: string = "";
  private themeCollectionSubject: BehaviorSubject<ThemeItem[]>;
  public themeCollection$ : Observable<ThemeItem[]>;
  // cambio de thema entre paginas
  private broadcastThemeSubscription: Subscription;


  private colorTheme = {
    Blue: '#',    // Azul clásico
    Yellow: '#',  // Amarillo dorado
    Green: '#',   // Verde brillante
    Red: '#'      // Rojo intenso
  }

  constructor(
    private localStorageService: LocalStorageService,
    private themeBroadcastService: ThemeBroadcastService,
    private translate: TranslateService
  ) {
    const themeSaved = this.loadSavedTheme() || this.defaultTheme
    this.currentThemeSubject = new BehaviorSubject<ThemeName>(
      themeSaved
    );
    this.currentTheme$ = this.currentThemeSubject.asObservable();

    this.themeCollectionSubject= new BehaviorSubject<ThemeItem[]>([])
    this.themeCollection$ = this.themeCollectionSubject.asObservable()

    this.loadThemes().then(()=>{
      this.colorSaved = this.loadColorTheme();
  
      // start theme
      this.selectTheme(this.currentThemeSubject.value)
    });
    const langSub = this.translate.onLangChange.subscribe(() => {
      this.loadThemes();
    });
    this.broadcastThemeSubscription = this.themeBroadcastService.getThemeChanges().subscribe(this.onThemeChange)
  }
  
  private loadThemes(): Promise<void> {
    const keys = [
      'CONFIGURATION.COLOR.BLUE',
      'CONFIGURATION.COLOR.YELLOW',
      'CONFIGURATION.COLOR.GREEN',
      'CONFIGURATION.COLOR.RED',
    ];

    return this.translate.get(keys).toPromise().then((translations) => {
      // Asignar traducciones al objeto colorTheme
      this.colorTheme = {
        Blue: translations['CONFIGURATION.COLOR.BLUE'],
        Yellow: translations['CONFIGURATION.COLOR.YELLOW'],
        Green: translations['CONFIGURATION.COLOR.GREEN'],
        Red: translations['CONFIGURATION.COLOR.RED'],
      };

      // Crear el arreglo de temas
      const themes: ThemeItem[] = [
        { name: ThemeName.Blue, label: this.colorTheme.Blue, theme: blueTheme },
        { name: ThemeName.Yellow, label: this.colorTheme.Yellow, theme: yellowTheme },
        { name: ThemeName.Green, label: this.colorTheme.Green, theme: greenTheme },
        { name: ThemeName.Red, label: this.colorTheme.Red, theme: redTheme },
      ];

      // Emitir los temas a través del BehaviorSubject
      this.themeCollectionSubject.next(themes);
    });
  }

  public getConfiguration() {
    return {
      colorSaved : this.colorSaved,
      theme : this.currentThemeSubject.getValue()
    }
  }

  ngOnDestroy(): void {
    this.broadcastThemeSubscription.unsubscribe();
  }

  private onThemeChange(data: { type: string; value: string }): void {
    const typeChange = data.type;
    if (typeChange == "theme") {
      if (data.value != this.currentThemeSubject.value) {
        this.applyTheme(data.value as ThemeName)
      }
    } else if (typeChange == "customColor") {
      if (data.value != this.getPrimaryColorFromTheme()) {
        this.changePrimaryColor(data.value)
      }
    }
  }

  // localStorage
  private loadSavedTheme(): ThemeName {
    return this.localStorageService.getItem(this.themeStorageKey)
  }
  private loadColorTheme(): string {
    return this.localStorageService.getItem(this.themeColorStorageKey)
  }
  private saveSelectedThemeToStorage(): void {
    this.localStorageService.setItem(this.themeStorageKey, this.currentThemeSubject.value)
  }
  private saveColorThemeToStorage(color: string) {
    this.localStorageService.setItem(this.themeColorStorageKey, color)
  }

  private applyTheme(nameTheme: ThemeName): boolean {
    const themeColors = this.findThemeByName(nameTheme)
    if (!themeColors) { return false }
    this.currentThemeSubject.next(nameTheme)
    this.changeTheme(themeColors)
    this.saveSelectedThemeToStorage()
    return true;
  }
  private changeTheme(theme: IThemeColors) {
    // Utilizamos el enum CSSVariables para asignar las variables CSS según el tema
    document.documentElement.style.setProperty(CSSVariables.GL_PRIMARY, theme.primary);
    document.documentElement.style.setProperty(CSSVariables.GL_SECONDARY, theme.secondary);
    document.documentElement.style.setProperty(CSSVariables.GL_TERTIARY, theme.tertiary);
    document.documentElement.style.setProperty(CSSVariables.GL_BODY_BACKGROUND_COLOR, theme.bodyBackgroundColor);
    document.documentElement.style.setProperty(CSSVariables.GL_SECONDARY_BACKGROUND, theme.secondaryBackground);

    document.documentElement.style.setProperty(CSSVariables.GL_HOVER, theme.hover);
    document.documentElement.style.setProperty(CSSVariables.GL_FOCUS, theme.focus);

    document.documentElement.style.setProperty(CSSVariables.GL_BORDER, theme.border);
    document.documentElement.style.setProperty(CSSVariables.GL_BORDER_HOVER, theme.borderHover);

    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_PRIMARY, theme.buttonPrimary);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_PRIMARY_HOVER, theme.buttonPrimaryHover);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_SECONDARY, theme.buttonSecondary);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_SECONDARY_HOVER, theme.buttonSecondaryHover);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_DANGER, theme.buttonDanger);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_DANGER_HOVER, theme.buttonDangerHover);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_TEXT, theme.buttonText);
    document.documentElement.style.setProperty(CSSVariables.GL_BUTTON_DISABLED, theme.buttonDisabled);

    document.documentElement.style.setProperty(CSSVariables.GL_CARD_BACKGROUND, theme.cardBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_CARD_SHADOW, theme.cardShadow);
    document.documentElement.style.setProperty(CSSVariables.GL_CARD_BORDER, theme.cardBorder);

    document.documentElement.style.setProperty(CSSVariables.GL_LOADING_SPINNER, theme.loadingSpinner);
    document.documentElement.style.setProperty(CSSVariables.GL_DISABLED, theme.disabled);

    document.documentElement.style.setProperty(CSSVariables.GL_HEADER_BACKGROUND, theme.headerBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_FOOTER_BACKGROUND, theme.footerBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_FOOTER_TEXT, theme.footerText);

    document.documentElement.style.setProperty(CSSVariables.GL_INFO_BACKGROUND, theme.infoBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_INFO_TEXT, theme.infoText);
    document.documentElement.style.setProperty(CSSVariables.GL_SUCCESS_BACKGROUND, theme.successBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_SUCCESS_TEXT, theme.successText);
    document.documentElement.style.setProperty(CSSVariables.GL_WARNING_BACKGROUND, theme.warningBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_WARNING_TEXT, theme.warningText);
    document.documentElement.style.setProperty(CSSVariables.GL_ERROR_BACKGROUND, theme.errorBackground);
    document.documentElement.style.setProperty(CSSVariables.GL_ERROR_TEXT, theme.errorText);

    document.documentElement.style.setProperty(CSSVariables.GL_TEXT_PRIMARY, theme.textPrimary);
    document.documentElement.style.setProperty(CSSVariables.GL_TEXT_SECONDARY, theme.textSecondary);
    document.documentElement.style.setProperty(CSSVariables.GL_TEXT_CONTRAST, theme.textContrast);

    document.documentElement.style.setProperty(CSSVariables.GL_LINK_TEXT, theme.linkText);
  }
  private findThemeByName(name: string): IThemeColors | undefined {
    console.log("theme selected list", this.themeCollectionSubject.value)
    const themeFound = this.themeCollectionSubject.value.find((theme) => theme.name == name)
    return themeFound?.theme
  }

  public selectTheme(nameTheme: ThemeName): boolean {

    if (nameTheme == ThemeName.Custom) {
      this.currentThemeSubject.next(nameTheme)
      this.saveSelectedThemeToStorage();
      this.colorSaved = this.loadColorTheme()
      if (this.colorSaved) {
        this.selectPrimaryColor(this.colorSaved)
      }
      return true;
    }

    console.log("theme selected", nameTheme)
    this.applyTheme(nameTheme)
    const themeSelected = this.findThemeByName(nameTheme);
    if (!themeSelected) { return false }
    
    this.themeBroadcastService.sendThemeChange(nameTheme);
    return true;
  }
  public applyColorTheme(colorVariable: CSSVariables, colorValue: string): void {
    document.documentElement.style.setProperty(colorVariable, colorValue);
  }

  public getCurrentTheme(): Observable<ThemeName> {
    return this.currentTheme$;
  }
  public getThemeOptions(): any[] {
    console.log(this.themeCollectionSubject.value, "themas personalizados")
    let options = this.themeCollectionSubject.value.map(theme => {
      return {
        name: theme.label,  // Nombre del tema
        value: theme.name  // Aquí puedes usar 'name' o un código si lo prefieres
      };
    });

    options.push(
      { name: ThemeName.Custom, value: ThemeName.Custom }
    )

    return options;
  }
  public getPrimaryColorFromTheme(): string {
    if (this.currentThemeSubject.value == ThemeName.Custom) {
      return this.colorSaved;
    }
    const themeColor = this.findThemeByName(this.currentThemeSubject.value)
    return themeColor?.primary || ""
  }

  public selectPrimaryColor(color: string) {
    this.colorSaved = color;
    this.changePrimaryColor(color)
    this.themeBroadcastService.sendCustomColorChange(color)
    this.saveColorThemeToStorage(color);
  }
  public changePrimaryColor(color: string) {
    const variants = this.generateTonalities(color)

    this.applyColorTheme(CSSVariables.GL_PRIMARY, color)
    this.applyColorTheme(CSSVariables.GL_SECONDARY, variants[0])
    this.applyColorTheme(CSSVariables.GL_TERTIARY, variants[1])
    this.applyColorTheme(CSSVariables.GL_BUTTON_PRIMARY, variants[2])
    this.applyColorTheme(CSSVariables.GL_BUTTON_SECONDARY, variants[3])
    this.applyColorTheme(CSSVariables.GL_HOVER, variants[4])
    this.applyColorTheme(CSSVariables.GL_FOCUS, variants[5])
    this.applyColorTheme(CSSVariables.GL_BUTTON_PRIMARY_HOVER, variants[6])
    this.applyColorTheme(CSSVariables.GL_BUTTON_SECONDARY_HOVER, variants[6])

  }

  private adjustColor(color: string, amount: number): string {
    let colorInt = parseInt(color.replace('#', ''), 16);

    let r = (colorInt >> 16) + amount;
    let g = ((colorInt >> 8) & 0x00FF) + amount;
    let b = (colorInt & 0x0000FF) + amount;

    // Limitar los valores a estar en el rango [0, 255]
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    // Convertir cada componente de color a su representación hexadecimal
    const hexR = r.toString(16).padStart(2, '0');  // Convertir a hexadecimal y asegurarse de que tenga 2 dígitos
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');

    // Concatenar y devolver el color ajustado en formato hexadecimal
    return `#${hexR}${hexG}${hexB}`.toUpperCase();
  }
  private generateTonalities(color: string): string[] {
    const tonalities = [];
    const tones = [-60, -40, -20, 0, 20, 40, 60]; // Variaciones en la cantidad de brillo

    for (const tone of tones) {
      tonalities.push(this.adjustColor(color, tone));
    }
    return tonalities; // retorna 7 colores
  }
}
